import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, LayoutGrid, SlidersHorizontal, Share2, Check } from 'lucide-react';
import Header from '../components/Header';
import SEOHead, { videoListSchema, breadcrumbSchema } from '../components/SEOHead';
import FilmPlayer from '../components/player/FilmPlayer';
import FavouriteButton from '../components/FavouriteButton';
import { trackView } from '../utils/viewTracker';
import '../components/theatre/theatre.css';
import { Label } from '../components/theatre/Cut';
import { CategoryChooser } from '../components/theatre/CategoryChooser';
import { QualityMenu } from '../components/theatre/QualityMenu';
import { Scrubber, Transport, MuteButton, FullscreenButton } from '../components/theatre/Controls';
import { UpNext } from '../components/theatre/UpNext';
import { LibraryModal } from '../components/theatre/LibraryModal';
import { PlaybackNotice, SoundPrompt } from '../components/theatre/PlaybackNotice';
import { useTheatreKeys, useSurfaceGestures } from '../components/theatre/useTheatreInput';
import { formatTime, isValidLogoUrl, DEFAULT_TAGS, sortFilms, hasTagMatch, parseTagsParam, readFilmsCache, writeFilmsCache } from '../components/theatre/theatreUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CHOOSER_AUTOHIDE_MS = 5000;

const copyText = async (text) => {
  if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(text); return; }
  const tmp = document.createElement('textarea'); tmp.value = text; tmp.style.position = 'fixed'; tmp.style.opacity = '0';
  document.body.appendChild(tmp); tmp.select(); document.execCommand('copy'); document.body.removeChild(tmp);
};

const FilmPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialTags = useRef(parseTagsParam(location.search));
  const initialFilmId = useRef(new URLSearchParams(location.search).get('film') || '');
  const deepLinked = initialTags.current.length > 0 || !!initialFilmId.current;

  const [logoUrl, setLogoUrl] = useState('');
  const [logoError, setLogoError] = useState(false);
  const [films, setFilms] = useState(() => readFilmsCache() || []);
  const [filteredFilms, setFilteredFilms] = useState(() => sortFilms(readFilmsCache() || [], initialTags.current));
  const [currentFilmIndex, setCurrentFilmIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  // Browsers block autoplay with sound — start muted and offer a clear unmute.
  const [isMuted, setIsMuted] = useState(true);
  const [soundBlocked, setSoundBlocked] = useState(true);
  const [showLibrary, setShowLibrary] = useState(false);
  const [showTagMenu, setShowTagMenu] = useState(!deepLinked);
  const [chooserAutoHide, setChooserAutoHide] = useState(!deepLinked);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoBuffered, setVideoBuffered] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isPortrait, setIsPortrait] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);
  const [playlistStarting, setPlaylistStarting] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState(initialTags.current);
  const [shareCopied, setShareCopied] = useState(false);
  const [filmShareCopied, setFilmShareCopied] = useState(false);
  const [engine, setEngine] = useState('none');
  const [engineNote, setEngineNote] = useState('');
  const [playbackFailed, setPlaybackFailed] = useState(false);
  const [quality, setQuality] = useState(null);
  const [showQualityMenu, setShowQualityMenu] = useState(false);

  const playerRef = useRef(null);
  const videoContainerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const engineNoteTimer = useRef(null);
  const exhaustTimer = useRef(null);
  const chooserTimer = useRef(null);
  const lastTagsKey = useRef(null);
  const currentFilmIdRef = useRef('');

  const currentFilm = filteredFilms[currentFilmIndex];
  currentFilmIdRef.current = currentFilm?.id || '';
  const hasPlayableSource = !!(currentFilm && (currentFilm.hls_url || currentFilm.video_url || currentFilm.video_url_low || currentFilm.video_url_high || currentFilm.youtube_id));

  // ---------- Device / orientation ----------
  useEffect(() => {
    let t;
    const measure = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        setIsPortrait(window.innerHeight > window.innerWidth);
        setIsMobile(window.innerWidth < 768 || ('ontouchstart' in window));
      }, 120);
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => { clearTimeout(t); window.removeEventListener('resize', measure); window.removeEventListener('orientationchange', measure); };
  }, []);

  // ---------- Data ----------
  useEffect(() => {
    (async () => {
      try {
        const [settingsRes, filmsRes, tagsRes] = await Promise.all([
          fetch(`${API_URL}/api/content/settings`),
          fetch(`${API_URL}/api/content/film-videos`).catch(() => ({ json: () => [] })),
          fetch(`${API_URL}/api/content/film-tags`).catch(() => ({ json: () => [] })),
        ]);
        const settingsData = await settingsRes.json();
        const filmsData = await filmsRes.json();
        const tagsData = await tagsRes.json();
        const filmLogo = settingsData.film_logo_url || settingsData.logo_url || '';
        setLogoUrl(isValidLogoUrl(filmLogo) ? filmLogo : '');
        if (Array.isArray(filmsData) && filmsData.length) { setFilms(filmsData); writeFilmsCache(filmsData); }
        setAvailableTags(Array.isArray(tagsData) && tagsData.length ? tagsData : DEFAULT_TAGS);
      } catch (e) {
        console.error('Theatre data error:', e);
        setAvailableTags(DEFAULT_TAGS);
      }
    })();
  }, []);

  // ---------- Playlist ----------
  useEffect(() => {
    const sorted = sortFilms(films, selectedTags);
    setFilteredFilms(sorted);
    const key = selectedTags.join(',');
    const tagsChanged = lastTagsKey.current !== null && lastTagsKey.current !== key;
    lastTagsKey.current = key;
    if (tagsChanged) initialFilmId.current = '';
    // A films refresh keeps the linked / current film; a new filter starts from the top.
    const keepId = initialFilmId.current || (!tagsChanged && currentFilmIdRef.current);
    const found = keepId ? sorted.findIndex((f) => f.id === keepId) : -1;
    setCurrentFilmIndex(found >= 0 ? found : 0);
  }, [selectedTags, films]);

  const clearExhaust = () => { if (exhaustTimer.current) { clearTimeout(exhaustTimer.current); exhaustTimer.current = null; } };
  useEffect(() => {
    clearExhaust();
    setEngineNote(''); setPlaybackFailed(false); setVideoBuffered(0); setFilmShareCopied(false);
    if (currentFilm?.id) trackView('film', currentFilm.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilmIndex, currentFilm?.id]);
  useEffect(() => () => { clearExhaust(); clearTimeout(engineNoteTimer.current); clearTimeout(chooserTimer.current); }, []);

  // Keep ?tags= in the address bar in sync (drop a stale ?film= once the visitor changes filters)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const next = selectedTags.length ? selectedTags.join(',') : '';
    if (next === (params.get('tags') || '')) return;
    if (next) params.set('tags', next); else params.delete('tags');
    params.delete('film');
    navigate({ pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTags]);

  // ---------- Chooser auto-hide: 5 s without a touch on it and the film takes over ----------
  const cancelChooserAutoHide = () => { if (chooserTimer.current) { clearTimeout(chooserTimer.current); chooserTimer.current = null; } setChooserAutoHide(false); };
  useEffect(() => {
    if (!showTagMenu || !chooserAutoHide || !filteredFilms.length) return undefined;
    chooserTimer.current = setTimeout(() => { setShowTagMenu(false); setChooserAutoHide(false); }, CHOOSER_AUTOHIDE_MS);
    return () => clearTimeout(chooserTimer.current);
  }, [showTagMenu, chooserAutoHide, filteredFilms.length]);

  // ---------- Controls visibility ----------
  const resetControlsTimeout = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    if (isPlaying && !showLibrary && engine !== 'youtube') controlsTimeoutRef.current = setTimeout(() => setShowControls(false), 3200);
  }, [isPlaying, showLibrary, engine]);
  useEffect(() => { resetControlsTimeout(); return () => clearTimeout(controlsTimeoutRef.current); }, [resetControlsTimeout]);
  useEffect(() => {
    const onMove = () => resetControlsTimeout();
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => window.removeEventListener('pointermove', onMove);
  }, [resetControlsTimeout]);

  const transitionToVideo = useCallback((newIndex) => {
    if (!filteredFilms.length) return;
    setIsFading(true);
    setTimeout(() => { setCurrentFilmIndex(newIndex); setIsFading(false); setVideoProgress(0); setVideoCurrentTime(0); }, 350);
  }, [filteredFilms.length]);
  const skipToNext = useCallback(() => { if (filteredFilms.length) transitionToVideo((currentFilmIndex + 1) % filteredFilms.length); }, [filteredFilms.length, currentFilmIndex, transitionToVideo]);
  const skipToPrevious = useCallback(() => { if (filteredFilms.length) transitionToVideo(currentFilmIndex === 0 ? filteredFilms.length - 1 : currentFilmIndex - 1); }, [filteredFilms.length, currentFilmIndex, transitionToVideo]);

  // One tap → this playlist, with sound. play() runs inside the tap so browsers unlock audio.
  const beginPlaylist = (tags) => {
    if (playlistStarting) return;
    setPlaylistStarting(true);
    cancelChooserAutoHide();
    setSelectedTags(tags);
    setIsMuted(false); setSoundBlocked(false);
    setShowTagMenu(false); setShowLibrary(false);
    setIsPlaying(true); setVideoLoading(true);
    resetControlsTimeout();
    playerRef.current?.play({ unmute: true });
    setTimeout(() => setPlaylistStarting(false), 450);
  };
  const pickTag = (tagName) => beginPlaylist([tagName]);
  const toggleTag = (tagName) => setSelectedTags((prev) => (prev.includes(tagName) ? prev.filter((t) => t !== tagName) : [...prev, tagName]));
  const pickFromLibrary = (index) => {
    setShowLibrary(false); setShowTagMenu(false); cancelChooserAutoHide();
    setIsMuted(false); setSoundBlocked(false); setIsPlaying(true);
    playerRef.current?.play({ unmute: true });
    transitionToVideo(index);
  };
  const openChooser = () => { setChooserAutoHide(false); setShowTagMenu(true); };

  const togglePlay = () => { if (isPlaying) playerRef.current?.pause(); else playerRef.current?.play(); setIsPlaying(!isPlaying); };
  const toggleMute = () => { const m = !isMuted; playerRef.current?.setMuted(m); setIsMuted(m); setSoundBlocked(false); if (!m) playerRef.current?.play({ unmute: true }); };
  const enableSound = () => { playerRef.current?.setMuted(false); setIsMuted(false); setSoundBlocked(false); playerRef.current?.play({ unmute: true }); };
  const seekBy = (delta) => { const d = playerRef.current?.getDuration() || videoDuration; const t = playerRef.current?.getCurrentTime() || 0; playerRef.current?.seekTo(Math.max(0, Math.min(d || Infinity, t + delta))); resetControlsTimeout(); };
  const seekToPercent = (pct) => { const d = playerRef.current?.getDuration() || videoDuration; if (d) playerRef.current?.seekTo(pct * d); };

  const toggleFullscreen = async () => {
    const video = playerRef.current?.getVideoElement?.();
    const container = videoContainerRef.current;
    const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
    try {
      if (fsEl) { if (document.exitFullscreen) await document.exitFullscreen(); else document.webkitExitFullscreen?.(); return; }
      if (video?.webkitEnterFullscreen && isMobile) video.webkitEnterFullscreen();
      else if (container?.requestFullscreen) await container.requestFullscreen();
      else if (container?.webkitRequestFullscreen) container.webkitRequestFullscreen();
      else video?.webkitEnterFullscreen?.();
    } catch (e) { video?.webkitEnterFullscreen?.(); }
  };

  const handleCopyShare = async () => {
    const url = `${window.location.origin}/film/theatre${selectedTags.length ? `?tags=${selectedTags.join(',')}` : ''}`;
    try { await copyText(url); setShareCopied(true); setTimeout(() => setShareCopied(false), 1800); } catch (e) { window.prompt('Copy this link:', url); }
  };
  const handleShareFilm = async () => {
    if (!currentFilm?.id) return;
    const url = `${window.location.origin}/film/theatre?film=${currentFilm.id}`;
    const title = `${currentFilm.title} — Crystal Media`;
    try {
      if (navigator.share && isMobile) { await navigator.share({ title, url }); return; }
      await copyText(url); setFilmShareCopied(true); setTimeout(() => setFilmShareCopied(false), 1800);
    } catch (e) { if (e?.name !== 'AbortError') window.prompt('Copy this link:', url); }
  };

  // ---------- Player callbacks ----------
  const handleEngineChange = (eng) => {
    setEngine(eng); setPlaybackFailed(false);
    let note = '';
    if (eng === 'youtube' && (currentFilm?.hls_url || currentFilm?.video_url)) note = 'Playing from YouTube for a steadier stream';
    else if (eng === 'mp4' && currentFilm?.hls_url) note = 'Adaptive stream unavailable — playing the standard file';
    setEngineNote(note);
    if (engineNoteTimer.current) clearTimeout(engineNoteTimer.current);
    if (note) engineNoteTimer.current = setTimeout(() => setEngineNote(''), 4500);
  };
  const handlePlayerTime = (t, d) => {
    setVideoCurrentTime(t || 0);
    if (d && d !== videoDuration) setVideoDuration(d);
    setVideoProgress(d ? (t / d) * 100 : 0);
    const v = playerRef.current?.getVideoElement?.();
    if (v && d && v.buffered?.length) { try { setVideoBuffered((v.buffered.end(v.buffered.length - 1) / d) * 100); } catch (_) { /* noop */ } }
  };
  const handleExhausted = (reason) => {
    setVideoLoading(false); setPlaybackFailed(true);
    setEngineNote(reason === 'unsupported' ? 'This browser cannot decode this film' : '');
    clearExhaust();
    if (filteredFilms.length > 1) exhaustTimer.current = setTimeout(() => skipToNext(), 4000);
  };
  const retryCurrent = () => { clearExhaust(); setPlaybackFailed(false); setEngineNote(''); setVideoLoading(true); playerRef.current?.retry(); };
  const handleAutoplayBlocked = () => { setIsMuted(true); setSoundBlocked(true); };

  const surface = useSurfaceGestures({
    onToggle: () => { if (!showTagMenu && engine !== 'youtube') togglePlay(); },
    onSeekBy: (d) => { if (!showTagMenu) seekBy(d); },
    onNext: skipToNext, onPrev: skipToPrevious, onActivity: resetControlsTimeout,
  });
  useTheatreKeys({
    togglePlay, toggleMute, toggleFullscreen, seekBy, next: skipToNext, prev: skipToPrevious,
    escape: () => { if (showLibrary) setShowLibrary(false); else if (showQualityMenu) setShowQualityMenu(false); else if (showTagMenu) { cancelChooserAutoHide(); setShowTagMenu(false); } },
  }, !showTagMenu && !showLibrary);

  const upcoming = () => {
    if (filteredFilms.length <= 1) return [];
    return Array.from({ length: Math.min(5, filteredFilms.length - 1) }, (_, i) => {
      const index = (currentFilmIndex + 1 + i) % filteredFilms.length;
      return { ...filteredFilms[index], playlistIndex: index };
    });
  };

  const player = (fit) => currentFilm && (
    <FilmPlayer
      ref={playerRef}
      film={currentFilm}
      muted={isMuted}
      autoplay={isPlaying}
      fit={fit}
      onPlay={() => setIsPlaying(true)}
      onPause={() => setIsPlaying(false)}
      onEnded={skipToNext}
      onTimeUpdate={handlePlayerTime}
      onDurationChange={(d) => { if (d && Number.isFinite(d)) setVideoDuration(d); }}
      onBuffering={(b) => setVideoLoading(!!b)}
      onEngineChange={handleEngineChange}
      onAutoplayBlocked={handleAutoplayBlocked}
      onExhausted={handleExhausted}
      onQuality={setQuality}
    />
  );

  const chooserProps = {
    tags: availableTags, selectedTags, onPick: pickTag, onPlayAll: () => beginPlaylist(selectedTags),
    onLibrary: () => { cancelChooserAutoHide(); setShowLibrary(true); }, onShare: handleCopyShare, shareCopied, starting: playlistStarting, filmCount: films.length,
    onClose: () => { cancelChooserAutoHide(); setShowTagMenu(false); }, onInteract: cancelChooserAutoHide, autoHideMs: chooserAutoHide ? CHOOSER_AUTOHIDE_MS : 0,
  };
  const noMatch = selectedTags.length > 0 && !hasTagMatch(films, selectedTags);
  const matchHint = noMatch ? <span className="th-caption" data-testid="film-no-match-hint">No films carry this tag yet — showing every film</span> : null;
  const notice = (testId) => (
    <PlaybackNotice note={engineNote} failed={playbackFailed} testId={testId} onRetry={retryCurrent}
      onDismiss={() => { clearExhaust(); setEngineNote(''); setPlaybackFailed(false); }} />
  );
  const shareFilmBtn = (
    <button type="button" onClick={handleShareFilm} className="th-icon-btn" aria-label="Share this film" data-testid="film-share-current-btn">
      {filmShareCopied ? <Check size={18} /> : <Share2 size={18} />}
    </button>
  );
  const portrait = isMobile && isPortrait;
  const counter = filteredFilms.length > 1 ? <span className="th-caption tabular-nums whitespace-nowrap" data-testid="film-now-playing-indicator">{currentFilmIndex + 1} of {filteredFilms.length}</span> : null;

  return (
    <div className="theatre fixed inset-0 overflow-hidden" style={{ background: 'var(--th-cinema)' }} data-testid="film-theatre" data-layout={portrait ? 'portrait' : 'landscape'}>
      <SEOHead
        title="Film Theatre — Cinematic Wedding Films"
        description="Watch Crystal Media's cinematic wedding films — Pakistani, Indian, Sikh, Punjabi, Muslim, Hindu and multicultural wedding highlights filmed in 8K across Toronto, Canada and the USA."
        url="/film/theatre"
        keywords={['wedding highlight films', 'Pakistani wedding highlights', 'Sikh wedding film Toronto', 'cinematic wedding cinematographer']}
        jsonLd={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Film', path: '/film' }, { name: 'Theatre', path: '/film/theatre' }]), ...(films.length ? [videoListSchema(films)] : [])]}
      />

      {portrait ? (
        <div className="flex flex-col h-full th-ground">
          <header className="flex-shrink-0 px-3 py-2 flex items-center justify-between z-30">
            <Link to="/film" className="th-icon-btn" aria-label="Back to film" data-testid="film-back-link"><ArrowLeft size={18} /></Link>
            <Link to="/film" className="flex items-center gap-2">
              {logoUrl && !logoError ? <img src={logoUrl} alt="Crystal Media" className="h-5 w-auto" onError={() => setLogoError(true)} /> : <Label>Crystal Media</Label>}
            </Link>
            <button type="button" onClick={() => setShowLibrary(true)} className="th-icon-btn" aria-label="Film library" data-testid="film-portrait-library-btn"><LayoutGrid size={18} /></button>
          </header>

          <div ref={videoContainerRef} data-engine={engine} className="flex-shrink-0 relative w-full flex items-center justify-center" style={{ height: 'calc(56.25vw)', background: 'var(--th-cinema)' }} {...surface}>
            <div className={`w-full h-full transition-opacity duration-500 ${isFading ? 'opacity-0' : 'opacity-100'}`}>{player('contain')}</div>
            {videoLoading && hasPlayableSource && <div className="absolute inset-0 pointer-events-none flex items-center justify-center" data-testid="film-portrait-loader"><div className="th-spinner" /></div>}
            <div className={`absolute inset-0 flex items-center justify-center pointer-events-none th-controls-enter ${showControls ? '' : 'th-controls-hidden'}`}>
              <div className="pointer-events-auto"><Transport isPlaying={isPlaying} onPrev={skipToPrevious} onNext={skipToNext} onToggle={togglePlay} size="lg" /></div>
            </div>
            {soundBlocked && <div className="absolute top-3 right-3 z-30"><SoundPrompt onEnable={enableSound} /></div>}
            {(engineNote || playbackFailed) && <div className="absolute left-2 right-2 bottom-2 z-[45]">{notice('film-engine-notice-portrait')}</div>}
          </div>

          <div className="flex-shrink-0 px-4 pt-1 pb-2" style={{ background: 'var(--th-espresso)' }}>
            <Scrubber progress={videoProgress} buffered={videoBuffered} onSeek={seekToPercent} testId="film-portrait-scrubber" />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <MuteButton muted={isMuted} onToggle={toggleMute} />
                <QualityMenu quality={quality} open={showQualityMenu} onToggle={setShowQualityMenu} onPick={(i) => playerRef.current?.setQuality(i)} up={false} />
                <span className="th-caption ml-2 tabular-nums whitespace-nowrap" data-testid="film-portrait-time">{formatTime(videoCurrentTime)} / {formatTime(videoDuration)}</span>
              </div>
              <div className="flex items-center gap-1">
                {counter}
                {currentFilm?.id && <FavouriteButton itemType="film" itemId={currentFilm.id} variant="light" size={16} />}
                {shareFilmBtn}
                <button type="button" onClick={openChooser} className="th-icon-btn" aria-label="Filters" data-testid="film-portrait-filters-btn"><SlidersHorizontal size={18} /></button>
                <FullscreenButton onToggle={toggleFullscreen} />
              </div>
            </div>
            {matchHint && <div className="pb-1">{matchHint}</div>}
          </div>

          <div className="flex-1 overflow-y-auto th-scroll" style={{ background: 'var(--th-espresso)' }}>
            {showTagMenu ? <CategoryChooser {...chooserProps} layout="sheet" testPrefix="film-portrait" /> : <UpNext films={upcoming()} onPick={transitionToVideo} currentIndex={currentFilmIndex} total={filteredFilms.length} />}
          </div>
        </div>
      ) : (
        <>
          <div ref={videoContainerRef} data-engine={engine} className="absolute inset-0" {...surface}>
            <div className={`w-full h-full transition-opacity duration-500 ${isFading ? 'opacity-0' : 'opacity-100'}`}>{player('cover')}</div>
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to top, rgba(7,6,5,0.78) 0%, rgba(7,6,5,0.08) 30%, rgba(7,6,5,0.25) 100%)' }} />
            {videoLoading && hasPlayableSource && !showTagMenu && <div className="absolute inset-0 pointer-events-none flex items-center justify-center z-20" data-testid="film-desktop-loader"><div className="th-spinner" /></div>}
            {(engineNote || playbackFailed) && <div className="absolute left-1/2 -translate-x-1/2 bottom-28 z-[45] w-full max-w-lg px-4">{notice('film-engine-notice-desktop')}</div>}
          </div>

          <div className="relative z-30"><Header logoUrl={logoUrl} variant="dark" /></div>
          {soundBlocked && <div className="fixed top-24 right-6 z-[46]"><SoundPrompt onEnable={enableSound} /></div>}

          {showTagMenu && <CategoryChooser {...chooserProps} layout="modal" testPrefix="film" />}

          <div className={`absolute bottom-0 left-0 right-0 z-30 th-controls-enter ${showControls || showTagMenu ? '' : 'th-controls-hidden'}`} data-testid="film-desktop-controls">
            <div className="px-6"><Scrubber progress={videoProgress} buffered={videoBuffered} onSeek={seekToPercent} testId="film-scrubber" /></div>
            <div className="px-6 pb-5 pt-1 flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Transport isPlaying={isPlaying} onPrev={skipToPrevious} onNext={skipToNext} onToggle={togglePlay} />
                <MuteButton muted={isMuted} onToggle={toggleMute} />
                <QualityMenu quality={quality} open={showQualityMenu} onToggle={setShowQualityMenu} onPick={(i) => playerRef.current?.setQuality(i)} />
                <span className="th-caption ml-3 tabular-nums" data-testid="film-desktop-time">{formatTime(videoCurrentTime)} / {formatTime(videoDuration)}</span>
                {counter && <span className="ml-3 hidden md:inline">{counter}</span>}
                {matchHint && <span className="ml-3 hidden lg:inline">{matchHint}</span>}
              </div>
              <div className="flex items-center gap-2">
                {currentFilm?.id && <FavouriteButton itemType="film" itemId={currentFilm.id} variant="light" size={16} />}
                {shareFilmBtn}
                {selectedTags.length > 0 && <span className="th-label text-[10px] hidden md:inline-flex items-center gap-2 whitespace-nowrap" data-testid="film-filter-count"><span className="w-1 h-1" style={{ background: 'var(--th-accent)' }} />{selectedTags.length} filter{selectedTags.length > 1 ? 's' : ''}</span>}
                <button type="button" onClick={openChooser} className="th-btn th-btn-text w-auto text-[11px] hidden sm:inline-flex" data-testid="film-filters-btn">Filters</button>
                <button type="button" onClick={() => setShowLibrary(true)} className="th-btn th-btn-text w-auto text-[11px]" data-testid="film-library-btn">Library</button>
                <FullscreenButton onToggle={toggleFullscreen} />
              </div>
            </div>
          </div>

          <Link to="/film" className="fixed top-1/2 -translate-y-1/2 left-0 z-40 hidden md:block th-glass px-4 py-8 th-label text-[10px] transition-colors duration-300 hover:bg-[rgba(216,177,125,0.1)]" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', color: 'var(--th-muted)', borderRight: '1px solid var(--th-hairline)' }} data-testid="exit-theatre-btn">
            Exit theatre
          </Link>
          <a href="/inquire" target="_blank" rel="noopener noreferrer" className="fixed top-1/2 -translate-y-1/2 right-0 z-40 hidden md:block th-glass px-4 py-8 th-label text-[10px] transition-colors duration-300 hover:bg-[rgba(216,177,125,0.1)]" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', borderLeft: '1px solid var(--th-hairline)' }} data-testid="film-inquire-tab">
            Inquire
          </a>
        </>
      )}

      {showLibrary && (
        <LibraryModal films={filteredFilms} allCount={films.length} tags={availableTags} selectedTags={selectedTags} onToggleTag={toggleTag} onClearTags={() => setSelectedTags([])}
          onPick={pickFromLibrary} currentIndex={currentFilmIndex} playing onClose={() => setShowLibrary(false)} />
      )}
    </div>
  );
};

export default FilmPage;
